import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useApi, useLoggedIn } from "@/hooks";

import { boostEligibilitySchema, type BoostEligibility } from "@/schemas";

import { BoostQueryKeys } from "../query-keys";

type UseBoostEligibilityOptions = {
  enabled?: boolean;
  staleTime?: number;
  refetchInterval?: number | false | ((data: BoostEligibility | undefined) => number | false);
};

// Default staleTime is slightly under the 30-minute tax_quote_token TTL so the
// cached quote is reused for the entire window it's valid for. Combined with
// focus/reconnect refetch disabled, this keeps /eligibility requests to one
// per intent-gated session.
const DEFAULT_ELIGIBILITY_STALE_TIME_MS = 25 * 60_000;

/**
 * Fetch boost eligibility and current price quote. When `statusId` is omitted
 * the eligibility endpoint returns a generic quote suitable for the composer
 * flow (post does not exist yet). The returned `taxQuoteToken` locks both
 * price and tax for 30 minutes.
 */
function useBoostEligibility(
  statusId?: string | null,
  options: UseBoostEligibilityOptions = {},
): UseQueryResult<BoostEligibility> {
  const api = useApi();
  const { isLoggedIn } = useLoggedIn();
  const {
    enabled = true,
    staleTime = DEFAULT_ELIGIBILITY_STALE_TIME_MS,
    refetchInterval,
  } = options;

  return useQuery<BoostEligibility, unknown, BoostEligibility>({
    queryKey: BoostQueryKeys.eligibility(statusId),
    queryFn: async ({ signal }): Promise<BoostEligibility> => {
      const params = statusId ? { status_id: statusId } : {};
      const response = await api.get("/api/v1/boosts/eligibility", {
        params,
        signal,
      });
      return boostEligibilitySchema.parse(response.data);
    },
    enabled: enabled && isLoggedIn,
    staleTime,
    // Keep inactive data cached for at least the stale window. react-query
    // garbage-collects an inactive query after `cacheTime`; if that were
    // shorter than `staleTime`, remounting the composer/menu after eviction
    // would force a fresh /eligibility fetch long before the quote actually
    // went stale — defeating the one-request-per-session goal above.
    cacheTime: staleTime,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval,
  });
}

export { useBoostEligibility };
